/** @jsx jsx */
import { jsx } from "theme-ui"
import PropTypes from "prop-types"

import {
  InstantSearch,
  InfiniteHits,
  Snippet,
  Highlight,
  RefinementList,
  connectStateResults,
  connectSearchBox,
  connectRefinementList,
} from "react-instantsearch-dom"

import { ab } from "../theme"
import Link from "./link"
import { SearchIcon } from "../assets/icons"
import algoliasearch from "algoliasearch/lite"
import Button from "./button"

const Search = ({
  active,
  indexName,
  refineLanguage,
  refineMode,
  appId,
  searchKey,
  buttonText,
  placeholder,
  closeAction,
  children,
  seoLinks,
  optionText,
}) => {
  const renderContentPreview = hit => {
    if (hit.metaDescription) {
      return <Snippet attribute="metaDescription" hit={hit} />
    }

    return <Snippet attribute="content" hit={hit} />
  }

  const Hit = ({ hit }) => (
    <article>
      <h2>
        <Link
          onClick={() => closeAction(false)}
          to={`/${hit.full_slug}`}
          language={"" + refineLanguage}
          seoLinks={seoLinks}
          sx={{
            color: "text",
            textDecoration: "none",
          }}
        >
          <Highlight attribute="name" hit={hit} />
        </Link>
      </h2>
      <p>{renderContentPreview(hit)}</p>
      <div
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Link
          onClick={() => closeAction(false)}
          language={"" + refineLanguage}
          seoLinks={seoLinks}
          sx={{
            color: "text",
            textDecoration: "none",
            borderBottom: "solid 1px",
            borderColor: "text",
            lineHeight: 1.2,
          }}
          to={`/${hit.full_slug}`}
        >
          Anzeigen
        </Link>
      </div>
    </article>
  )

  const Results = connectStateResults(
    ({ searchState: state, searchResults: res, children }) => {
      if (!res || res.query === "") {
        return null
      }

      return res && res.nbHits > 0
        ? children
        : `No results for '${state.query}'`
    }
  )

  const SearchBox = ({ currentRefinement, refine }) => (
    <div
      sx={{
        pt: 2,
        borderBottom: "solid 1px",
        borderColor: "text",
        display: "flex",
        color: "text",
        "&::placeholder": {
          color: "#b1b1b1",
        },
      }}
    >
      <input
        type="search"
        value={currentRefinement}
        onChange={event => refine(event.currentTarget.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        maxLength="512"
        placeholder={placeholder}
        sx={{
          flexGrow: 1,
          border: "none",
          p: 1,
          fontSize: 5,
          "&:focus": {
            outline: 0,
          },
        }}
        data-cy-identifier="search-input"
      />
      <button
        type="submit"
        sx={{
          bg: "#fff",
          border: "none",
          fontSize: 3,
          color: "text",
          verticalAlign: "baseline",
          cursor: "pointer",
          marginLeft: "-40px",
        }}
      >
        <span
          sx={{
            textIndent: ab({ _: -999, sm: 0 }),
            display: "inline-block",
          }}
        >
          {buttonText}
        </span>
        <span
          sx={{
            display: ab({ _: "inline-block", sm: "none" }),
            mb: "5px",
            mr: "5px",
            textAlign: "center",
            width: 24,
            height: 24,
            maxWidth: 30,
          }}
        >
          <SearchIcon />
        </span>
      </button>
    </div>
  )
  const CustomSearchBox = connectSearchBox(SearchBox)

  const Stats = connectStateResults(
    ({ searchResults: res }) =>
      res &&
      res.query !== "" &&
      res.nbHits > 0 &&
      `${res.nbHits} result${res.nbHits > 1 ? `s` : ``} für ${res.query}`
  )

  const ModeRefinementList = ({ currentRefinement, refine }) => {
    const toggleSearchMode = mode => {
      const modeIndex = currentRefinement.indexOf(mode)

      if (modeIndex > -1) {
        return currentRefinement.filter(currentMode => currentMode !== mode)
      } else {
        return [...currentRefinement, mode]
      }
    }

    const buttonActive = mode => {
      return !!(currentRefinement.indexOf(mode) > -1)
    }
    return (
      <div className="refinements-controls">
        <ul
          sx={{
            m: 0,
            p: 0,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            "& li": {
              listStyle: "none",
              minWidth: ab({ _: "49%", sm: "23%" }),
              marginBottom: ab({ _: "10px" }),
              "& button": {
                border: "solid 1px #ccc",
                borderRadius: "30px",
                color: "#ccc",
                width: "100%",
              },
              "&.active": {
                "& button": {
                  color: "#fff",
                  border: 0,
                },
              },
            },
          }}
        >
          <li className={buttonActive("group") ? "active" : ""}>
            <Button
              onClick={() => refine(toggleSearchMode("group"))}
              buttonText="GBA Group"
              style={{
                backgroundColor: buttonActive("group") ? `primary` : "#fff",
              }}
            />
          </li>
          <li className={buttonActive("pharma") ? "active" : ""}>
            <Button
              onClick={() => refine(toggleSearchMode("pharma"))}
              buttonText="Pharma"
              style={{
                backgroundColor: buttonActive("pharma")
                  ? "modes.pharma.primary"
                  : "#fff",
              }}
            />
          </li>
          <li className={buttonActive("food") ? "active" : ""}>
            <Button
              onClick={() => refine(toggleSearchMode("food"))}
              buttonText="Food"
              style={{
                backgroundColor: buttonActive("food")
                  ? "modes.food.primary"
                  : "#fff",
              }}
            />
          </li>
          <li className={buttonActive("environment") ? "active" : ""}>
            <Button
              onClick={() => refine(toggleSearchMode("environment"))}
              buttonText="Environment"
              style={{
                backgroundColor: buttonActive("environment")
                  ? "modes.environment.primary"
                  : "#fff",
              }}
            />
          </li>
        </ul>
      </div>
    )
  }

  const CustomRefinementList = connectRefinementList(ModeRefinementList)

  if (!active) {
    return null
  } else {
    const searchClient = algoliasearch(appId, searchKey)

    return (
      <InstantSearch searchClient={searchClient} indexName={indexName}>
        <div className="search-panel">
          <div className="search-panel__results">
            <div className="refinements" style={{ display: "none" }}>
              <RefinementList
                attribute="language"
                defaultRefinement={refineLanguage}
              />
            </div>
            <CustomSearchBox
              className="searchbox"
              translations={{
                placeholder: "",
              }}
            />
            {children}
            <p>{optionText}</p>

            <CustomRefinementList
              attribute="mode"
              defaultRefinement={refineMode}
            />
            <div
              className="result-list"
              sx={{
                pb: 5,
              }}
            >
              <Results>
                <div
                  sx={{
                    py: 3,
                  }}
                >
                  <Stats />
                </div>
                <div
                  className="search-hits"
                  sx={{
                    "& ul": {
                      margin: 0,
                      padding: 0,
                      listStyleType: "none",
                    },
                    "& .ais-InfiniteHits-loadMore--disabled": {
                      display: "none",
                    },
                  }}
                >
                  <InfiniteHits hitComponent={Hit} />
                </div>
              </Results>
            </div>
          </div>
        </div>
      </InstantSearch>
    )
  }
}

Search.defaultProps = {
  seoLinks: {},
}

Search.propTypes = {
  active: PropTypes.bool.isRequired,
  indexName: PropTypes.string.isRequired,
  refineLanguage: PropTypes.array.isRequired,
  refineMode: PropTypes.array.isRequired,
  appId: PropTypes.string.isRequired,
  searchKey: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  closeAction: PropTypes.func.isRequired,
  children: PropTypes.node,
  seoLinks: PropTypes.object.isRequired,
  optionText: PropTypes.string.isRequired,
}

export default Search
